import React from "react";
import { useLocation, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  canVisitPath, pageForPath, useCurrentAdminRole, whoCanPerform,
} from "@/lib/permissions";

// Roles allowed to visit the given page key, used in the 403 copy.
const PAGE_ROLES = {
  dashboard:    ["Super Admin", "Deal Manager", "Payments Overseer", "Read-only Operator"],
  audit:        ["Super Admin", "Deal Manager", "Payments Overseer", "Read-only Operator"],
  deals:        ["Super Admin", "Deal Manager", "Read-only Operator"],
  applications: ["Super Admin", "Deal Manager", "Read-only Operator"],
  submissions:  ["Super Admin", "Deal Manager", "Read-only Operator"],
  brands:       ["Super Admin", "Deal Manager", "Read-only Operator"],
  payments:     ["Super Admin", "Payments Overseer", "Read-only Operator"],
  members:      ["Super Admin", "Read-only Operator"],
  settings:     ["Super Admin"],
};

// Wraps every admin route element. If the current admin's role lacks access
// to the page key, renders a clean 403 screen INSIDE the AdminShell instead
// of the page content.
export function PageGate({ children }) {
  const location = useLocation();
  const role = useCurrentAdminRole();
  if (canVisitPath(location.pathname, role)) return children;

  const page = pageForPath(location.pathname);
  const eligible = PAGE_ROLES[page] || ["Super Admin"];
  const rolesText = eligible.length === 1
    ? eligible[0]
    : eligible.slice(0, -1).join(", ") + " or " + eligible[eligible.length - 1];

  return (
    <div data-testid="page-403" data-current-role={role} data-attempted-page={page} className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md text-center space-y-4 p-8 bg-surface border border-line rounded-xl">
        <div className="size-12 rounded-full bg-red-50 text-red-600 grid place-items-center mx-auto">
          <Lock className="size-5" />
        </div>
        <div>
          <div className="eyebrow uppercase text-red-700">403 · Access denied</div>
          <h1 className="font-brand text-h2 text-ink mt-1">Your role doesn&rsquo;t have access to this page.</h1>
        </div>
        <p className="text-body-sm text-ink-muted leading-relaxed">
          You&rsquo;re signed in as <span className="font-semibold text-ink">{role}</span>.
          This page is available to {rolesText}.
        </p>
        <div className="flex items-center justify-center gap-2 pt-2">
          <Link to="/admin/dashboard">
            <Button variant="outline" data-testid="page-403-back-btn">
              <ArrowLeft className="size-4 mr-1.5" /> Back to dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
